import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Radio, Dropdown, Modal, Form, Input, Drawer, message, Space } from 'antd';
import type { MenuProps } from 'antd';
import {
  UploadOutlined,
  SearchOutlined,
  PictureOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuOutlined, // 引入汉堡菜单图标
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import UploadPage from './UploadPage';
import SearchPage from './SearchPage';
import GalleryPage from './GalleryPage';
import InviteManagePage from './InviteManagePage';
import MobileSearchPage from './MobileSearchPage'; // 引入移动端搜索页面
import './MobileSearchPage.css';
import MobileGalleryPage from './MobileGalleryPage'; // 引入移动端图库页面
import MobileUploadPage from './MobileUploadPage'; // 引入移动端上传页面
import MobileInviteManagePage from './MobileInviteManagePage'; // 引入移动端邀请码管理页面
import { getCurrentUser } from '../services/api';
import { changePassword } from '../services/api';
import './DashboardPage.css';
import { useMode } from '../context/ModeContext';

const { Header, Sider, Content } = Layout;

const DashboardPage: React.FC = () => {
  const [selectedKey, setSelectedKey] = useState('search'); // 默认选中搜索
  const [isAdmin, setIsAdmin] = useState(false);
  const [username, setUsername] = useState<string>('');
  const [pwdVisible, setPwdVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false); // 控制抽屉菜单的显示
  const [pwdForm] = Form.useForm();
  const { mode, setMode } = useMode(); // 使用全局状态

  useEffect(() => {
    (async () => {
      try {
        const me = await getCurrentUser();
        setIsAdmin(!!me?.is_admin);
        setUsername(me?.username || '');
      } catch { }
    })();
  }, []);
  const navigate = useNavigate();

  // 处理登出
  const handleLogout = () => {
    Cookies.remove('token');
    message.success('已退出登录');
    navigate('/login');
  };

  const menuItems = [
    {
      key: 'search',
      icon: <SearchOutlined />,
      label: '图片搜索',
    },
    {
      key: 'upload',
      icon: <UploadOutlined />,
      label: '图片上传',
    },
    {
      key: 'gallery',
      icon: <PictureOutlined />,
      label: '图片管理',
    },
    ...(isAdmin ? [{ key: 'invites', icon: <UserOutlined />, label: '邀请码管理' }] : []),
  ];

  // 渲染页面内容
  const renderContent = () => {
    // 根据模式决定加载哪个搜索组件
    if (mode === 'mobile') {
      switch (selectedKey) {
        case 'search':
          return <MobileSearchPage />;
        // 其他移动端页面待实现
        case 'upload':
          return <MobileUploadPage />; // 使用移动端上传页面
        case 'gallery':
          return <MobileGalleryPage />; // 使用移动端图库页面
        case 'invites':
          return isAdmin ? <MobileInviteManagePage /> : null; // 使用移动端邀请码管理页面
        default:
          return <MobileSearchPage />;
      }
    }

    // 桌面端默认逻辑
    switch (selectedKey) {
      case 'upload':
        return <UploadPage />;
      case 'search':
        return <SearchPage />;
      case 'gallery':
        return <GalleryPage />;
      case 'invites':
        return isAdmin ? <InviteManagePage /> : null; // 非管理员不显示任何内容
      default:
        return <SearchPage />;
    }
  };

  const menu = (
    <Menu
      mode="inline"
      selectedKeys={[selectedKey]}
      onSelect={({ key }) => {
        setSelectedKey(key);
        if (mode === 'mobile') {
          setDrawerVisible(false); // 手机模式下点击菜单项后关闭抽屉
        }
      }}
      className="dashboard-menu"
      items={menuItems}
    />
  );

  return (
    <Layout className={`dashboard-layout ${mode === 'mobile' ? 'mobile-mode' : ''}`}>
      {/* 顶部导航 */}
      <Header className="dashboard-header">
        <div className="header-content" style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {mode === 'mobile' && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerVisible(true)}
                style={{ color: 'white', marginRight: '16px' }}
              />
            )}
            <h1 className="header-title" style={{ margin: 0 }}>
              BOSIMAO查询系统
            </h1>
          </div>
          <div className="header-right">
            <Dropdown
              menu={{
                items: [
                  { key: 'user', label: `当前用户：${username}`, disabled: true, icon: <UserOutlined /> },
                  { type: 'divider' },
                  { key: 'changePwd', label: '修改密码' },
                  { key: 'logout', label: '退出登录', icon: <LogoutOutlined /> },
                ],
                onClick: async ({ key }) => {
                  if (key === 'logout') {
                    handleLogout();
                  } else if (key === 'changePwd') {
                    setPwdVisible(true);
                  }
                },
              } as MenuProps}
            >
              <Button type="text" icon={<UserOutlined />}>
                {username || '用户'}
              </Button>
            </Dropdown>
            {/* 隐藏模式切换按钮（桌面端与手机端均不显示） */}
          </div>
        </div>
      </Header>

      <Layout>
        {/* 侧边栏菜单 - 仅在桌面模式下显示 */}
        {mode === 'desktop' && (
          <Sider width={200} className="dashboard-sider">
            {menu}
          </Sider>
        )}

        {/* 移动端抽屉菜单 */}
        <Drawer
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          bodyStyle={{ padding: 0 }}
          width={200}
        >
          {menu}
        </Drawer>

        {/* 主内容区域 */}
        <Content className="dashboard-content">
          <div className="content-wrapper">
            {renderContent()}
          </div>
        </Content>
      </Layout>

      <Modal
        title="修改密码"
        open={pwdVisible}
        onOk={async () => {
          try {
            const vals = await pwdForm.validateFields();
            await changePassword(vals.old_password, vals.new_password);
            message.success('密码修改成功，请重新登录');
            setPwdVisible(false);
            Cookies.remove('token');
            navigate('/login');
          } catch {}
        }}
        onCancel={() => setPwdVisible(false)}
        okText="提交"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={pwdForm} layout="vertical">
          <Form.Item label="当前密码" name="old_password" rules={[{ required: true, message: '请输入当前密码' }]}>
            <Input.Password placeholder="当前密码" />
          </Form.Item>
          <Form.Item label="新密码" name="new_password" rules={[{ required: true, message: '请输入新密码' }] }>
            <Input.Password placeholder="新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default DashboardPage;
